import { useEffect, useRef, useState } from 'react';

import {
  faChevronLeft,
  faChevronRight,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import { cn } from '../lib/utils';

interface ScrollableTabsProps {
  children: React.ReactNode;
  mode?: 'dark' | 'light';
  className?: string;
  scrollAmount?: number;
  /**
   * Optional dependency array to trigger scroll check recalculation
   * Useful when tabs content changes dynamically
   */
  dependencies?: unknown[];
}

export const ScrollableTabs = ({
  children,
  mode = 'dark',
  className,
  scrollAmount = 200,
  dependencies = [],
}: ScrollableTabsProps) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Check initial state after rendering
    const timeoutId = setTimeout(() => {
      checkScroll();
    }, 0);

    // Check on resize
    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });
    resizeObserver.observe(scrollContainer);

    scrollContainer.addEventListener('scroll', checkScroll);

    return () => {
      clearTimeout(timeoutId);
      scrollContainer.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [dependencies]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      className={cn(
        'vai-relative vai-w-full vai-max-w-full vai-min-w-0 vai-overflow-hidden',
        className,
      )}
    >
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          className={clsx(
            'vai-absolute vai-left-0 vai-top-0 vai-z-30 vai-h-full vai-flex vai-items-center vai-px-3 vai-transition-opacity hover:vai-opacity-80 vai-pointer-events-auto',
            {
              'vai-bg-gradient-to-r vai-from-zinc-950 vai-to-transparent':
                mode === 'dark',
              'vai-bg-gradient-to-r vai-from-white vai-to-transparent':
                mode === 'light',
            },
          )}
          onClick={scrollLeft}
          type="button"
          aria-label="Scroll tabs left"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className={clsx('vai-text-lg', {
              'vai-text-zinc-400': mode === 'dark',
              'vai-text-zinc-500': mode === 'light',
            })}
          />
        </button>
      )}

      {/* Right Arrow */}
      {showRightArrow && (
        <button
          className={clsx(
            'vai-absolute vai-right-0 vai-top-0 vai-z-30 vai-h-full vai-flex vai-items-center vai-px-3 vai-transition-opacity hover:vai-opacity-80 vai-pointer-events-auto',
            {
              'vai-bg-gradient-to-l vai-from-zinc-950 vai-to-transparent':
                mode === 'dark',
              'vai-bg-gradient-to-l vai-from-white vai-to-transparent':
                mode === 'light',
            },
          )}
          onClick={scrollRight}
          type="button"
          aria-label="Scroll tabs right"
        >
          <FontAwesomeIcon
            icon={faChevronRight}
            className={clsx('vai-text-lg', {
              'vai-text-zinc-400': mode === 'dark',
              'vai-text-zinc-500': mode === 'light',
            })}
          />
        </button>
      )}

      {/* Scrollable Tabs Container */}
      <div
        ref={scrollContainerRef}
        className="vai-overflow-x-auto vai-overflow-y-hidden vai-w-full vai-max-w-full vai-min-w-0 [&::-webkit-scrollbar]:vai-hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
};
